import React, { useState, useEffect, useRef } from 'react';
import {
  Users,
  Upload,
  Download,
  Trash2,
  RefreshCw,
  AlertCircle,
  Check,
  X,
  FileSpreadsheet,
  Search,
} from 'lucide-react';
import * as XLSX from 'xlsx';
import { useTheme } from '../context/ThemeContext';
import {
  getDraw,
  importDraw,
  deleteDrawPlayer,
  clearDraw,
} from '../services/drawService';

const AdminTournamentDraw = ({ tournamentId, tournamentName }) => {
  const { isDarkMode } = useTheme();

  // Data
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Search
  const [searchTerm, setSearchTerm] = useState('');

  // Import modal
  const [showImport, setShowImport] = useState(false);
  const [importData, setImportData] = useState([]);
  const [importFileName, setImportFileName] = useState('');
  const fileInputRef = useRef(null);

  // Load draw on mount
  useEffect(() => {
    if (tournamentId) {
      loadDraw();
    }
  }, [tournamentId]);

  const loadDraw = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getDraw(tournamentId);
      setPlayers(response.data || []);
    } catch (err) {
      setError(err.message);
      setPlayers([]);
    } finally {
      setLoading(false);
    }
  };

  const showMessage = (msg, isError = false) => {
    if (isError) {
      setError(msg);
      setSuccess(null);
    } else {
      setSuccess(msg);
      setError(null);
    }
    setTimeout(() => {
      setError(null);
      setSuccess(null);
    }, 3000);
  };

  // Handle Excel file selection
  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setImportFileName(file.name);

    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const data = evt.target.result;
        const workbook = XLSX.read(data, { type: 'binary' });
        const sheetName = workbook.SheetNames[0];
        const sheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(sheet, { header: 1 });

        // Skip header row and parse data
        const parsed = [];
        for (let i = 1; i < jsonData.length; i++) {
          const row = jsonData[i];
          if (row[0]) {
            parsed.push({
              player_name: String(row[0] || '').trim(),
              club: String(row[1] || '').trim(),
              handicap: row[2] !== undefined && row[2] !== '' ? parseFloat(row[2]) : null,
              tee_time: String(row[3] || '').trim(),
              group_number: row[4] !== undefined && row[4] !== '' ? parseInt(row[4]) : null,
              starting_tee: String(row[5] || '').trim(),
            });
          }
        }

        setImportData(parsed);
        showMessage(`Found ${parsed.length} players in file`);
      } catch (err) {
        showMessage('Error reading file: ' + err.message, true);
        setImportData([]);
      }
    };
    reader.readAsBinaryString(file);
  };

  // Import data to database
  const handleImport = async () => {
    if (importData.length === 0) {
      showMessage('No data to import. Please select an Excel file first.', true);
      return;
    }

    if (
      players.length > 0 &&
      !window.confirm(
        `This will replace the existing ${players.length} players with ${importData.length} new entries. Continue?`
      )
    ) {
      return;
    }

    try {
      setLoading(true);
      await importDraw(tournamentId, importData, true);

      setShowImport(false);
      setImportData([]);
      setImportFileName('');
      if (fileInputRef.current) fileInputRef.current.value = '';
      showMessage(`Imported ${importData.length} players`);
      loadDraw();
    } catch (err) {
      showMessage(err.message, true);
      setLoading(false);
    }
  };

  // Download Excel template
  const handleDownloadTemplate = () => {
    const templateData = [
      ['Player Name', 'Club', 'Handicap', 'Tee Time', 'Group', 'Starting Tee'],
      ['John Smith', 'Bloemfontein GC', 5.2, '07:00', 1, '1'],
      ['Jane Doe', 'Welkom GC', 12.5, '07:00', 1, '1'],
      ['Bob Wilson', 'Bloem GC', 8.0, '07:10', 2, '1'],
    ];

    const ws = XLSX.utils.aoa_to_sheet(templateData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Draw Template');
    XLSX.writeFile(wb, `Draw_Template.xlsx`);
  };

  // Export current draw to Excel
  const handleExport = () => {
    if (players.length === 0) {
      showMessage('No players to export', true);
      return;
    }

    const exportData = [
      ['Player Name', 'Club', 'Handicap', 'Tee Time', 'Group', 'Starting Tee'],
      ...players.map((p) => [
        p.player_name,
        p.club || '',
        p.handicap || '',
        p.tee_time || '',
        p.group_number || '',
        p.starting_tee || '',
      ]),
    ];

    const ws = XLSX.utils.aoa_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Draw');
    XLSX.writeFile(wb, `Draw_${tournamentId}.xlsx`);
  };

  // Delete single player
  const handleDeletePlayer = async (id, playerName) => {
    if (!window.confirm(`Remove ${playerName} from draw?`)) return;

    try {
      await deleteDrawPlayer(id);
      showMessage('Player removed');
      loadDraw();
    } catch (err) {
      showMessage(err.message, true);
    }
  };

  // Clear entire draw
  const handleClearDraw = async () => {
    if (!window.confirm(`Remove all ${players.length} players from the draw? This cannot be undone.`)) return;

    try {
      setLoading(true);
      await clearDraw(tournamentId);
      showMessage('Draw cleared');
      loadDraw();
    } catch (err) {
      showMessage(err.message, true);
      setLoading(false);
    }
  };

  // Filter players by search
  const filteredPlayers = players.filter(
    (p) =>
      p.player_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (p.club && p.club.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-3">
          <div
            className={`p-2 rounded-lg ${
              isDarkMode ? 'bg-fsgu-blue-500/20' : 'bg-fsgu-blue-500/10'
            }`}
          >
            <Users
              className={`w-5 h-5 ${
                isDarkMode ? 'text-fsgu-blue-400' : 'text-fsgu-blue-600'
              }`}
            />
          </div>
          <div>
            <h3
              className={`text-lg font-semibold ${
                isDarkMode ? 'text-fsgu-silver-100' : 'text-fsgu-silver-900'
              }`}
            >
              Tournament Draw
            </h3>
            <p
              className={`text-sm ${
                isDarkMode ? 'text-fsgu-silver-400' : 'text-fsgu-silver-600'
              }`}
            >
              {players.length} players registered
            </p>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowImport(true)}
            className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
              isDarkMode
                ? 'bg-fsgu-green-500/20 text-fsgu-green-400 hover:bg-fsgu-green-500/30'
                : 'bg-fsgu-green-500/10 text-fsgu-green-600 hover:bg-fsgu-green-500/20'
            }`}
          >
            <Upload className="w-4 h-4" />
            Import
          </button>

          {players.length > 0 && (
            <>
              <button
                onClick={handleExport}
                className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  isDarkMode
                    ? 'bg-fsgu-gray-700 text-fsgu-silver-300 hover:bg-fsgu-gray-600'
                    : 'bg-fsgu-silver-100 text-fsgu-silver-700 hover:bg-fsgu-silver-200'
                }`}
              >
                <Download className="w-4 h-4" />
                Export
              </button>

              <button
                onClick={handleClearDraw}
                className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  isDarkMode
                    ? 'bg-red-500/20 text-red-400 hover:bg-red-500/30'
                    : 'bg-red-500/10 text-red-600 hover:bg-red-500/20'
                }`}
              >
                <Trash2 className="w-4 h-4" />
                Clear
              </button>
            </>
          )}
        </div>
      </div>

      {/* Messages */}
      {error && (
        <div className="flex items-center gap-2 p-3 rounded-lg bg-red-500/10 text-red-500">
          <AlertCircle className="w-5 h-5 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {success && (
        <div className="flex items-center gap-2 p-3 rounded-lg bg-green-500/10 text-green-500">
          <Check className="w-5 h-5 flex-shrink-0" />
          <span>{success}</span>
        </div>
      )}

      {/* Search */}
      {players.length > 0 && (
        <div className="relative">
          <Search
            className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${
              isDarkMode ? 'text-fsgu-silver-500' : 'text-fsgu-silver-400'
            }`}
          />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search players..."
            className={`input w-full pl-10 ${
              isDarkMode ? 'bg-fsgu-gray-800' : 'bg-white'
            }`}
          />
        </div>
      )}

      {/* Loading State */}
      {loading && players.length === 0 && (
        <div className="py-12 text-center">
          <RefreshCw
            className={`w-8 h-8 mx-auto mb-3 animate-spin ${
              isDarkMode ? 'text-fsgu-silver-500' : 'text-fsgu-silver-400'
            }`}
          />
          <p className={isDarkMode ? 'text-fsgu-silver-400' : 'text-fsgu-silver-600'}>
            Loading draw...
          </p>
        </div>
      )}

      {/* Empty State */}
      {!loading && players.length === 0 && (
        <div
          className={`py-12 text-center rounded-xl border-2 border-dashed ${
            isDarkMode ? 'border-fsgu-gray-700 bg-fsgu-gray-800/50' : 'border-fsgu-silver-200 bg-fsgu-silver-50'
          }`}
        >
          <Users
            className={`w-12 h-12 mx-auto mb-3 ${
              isDarkMode ? 'text-fsgu-silver-600' : 'text-fsgu-silver-300'
            }`}
          />
          <p
            className={`text-lg font-medium mb-1 ${
              isDarkMode ? 'text-fsgu-silver-300' : 'text-fsgu-silver-700'
            }`}
          >
            No players in draw
          </p>
          <p className={`mb-4 ${isDarkMode ? 'text-fsgu-silver-500' : 'text-fsgu-silver-500'}`}>
            Import players from Golf Genius export
          </p>
          <button
            onClick={() => setShowImport(true)}
            className="btn btn-primary"
          >
            <Upload className="w-4 h-4 mr-2" />
            Import Players
          </button>
        </div>
      )}

      {/* Players Table */}
      {players.length > 0 && (
        <div
          className={`rounded-xl border overflow-hidden ${
            isDarkMode ? 'bg-fsgu-gray-800 border-fsgu-gray-700' : 'bg-white border-fsgu-silver-200'
          }`}
        >
          {/* Table Header */}
          <div
            className={`grid grid-cols-12 gap-2 px-4 py-3 text-sm font-medium border-b ${
              isDarkMode
                ? 'bg-fsgu-gray-900 border-fsgu-gray-700 text-fsgu-silver-400'
                : 'bg-fsgu-silver-50 border-fsgu-silver-200 text-fsgu-silver-600'
            }`}
          >
            <div className="col-span-4">Player</div>
            <div className="col-span-3">Club</div>
            <div className="col-span-1 text-center">HCP</div>
            <div className="col-span-1 text-center">Tee</div>
            <div className="col-span-1 text-center">Group</div>
            <div className="col-span-2 text-right">Actions</div>
          </div>

          {/* Data Rows */}
          {filteredPlayers.map((player) => (
            <div
              key={player.id}
              className={`grid grid-cols-12 gap-2 px-4 py-3 items-center border-b last:border-b-0 ${
                isDarkMode
                  ? 'border-fsgu-gray-700 hover:bg-fsgu-gray-700/50'
                  : 'border-fsgu-silver-100 hover:bg-fsgu-silver-50'
              }`}
            >
              <div className="col-span-4">
                <span
                  className={`font-medium ${
                    isDarkMode ? 'text-fsgu-silver-100' : 'text-fsgu-silver-900'
                  }`}
                >
                  {player.player_name}
                </span>
              </div>
              <div className="col-span-3">
                <span className={isDarkMode ? 'text-fsgu-silver-400' : 'text-fsgu-silver-600'}>
                  {player.club || '-'}
                </span>
              </div>
              <div className="col-span-1 text-center">
                <span className={isDarkMode ? 'text-fsgu-silver-400' : 'text-fsgu-silver-600'}>
                  {player.handicap !== null ? player.handicap : '-'}
                </span>
              </div>
              <div className="col-span-1 text-center">
                <span className={isDarkMode ? 'text-fsgu-silver-400' : 'text-fsgu-silver-600'}>
                  {player.tee_time || '-'}
                </span>
              </div>
              <div className="col-span-1 text-center">
                <span className={isDarkMode ? 'text-fsgu-silver-400' : 'text-fsgu-silver-600'}>
                  {player.group_number || '-'}
                </span>
              </div>
              <div className="col-span-2 flex justify-end">
                <button
                  onClick={() => handleDeletePlayer(player.id, player.player_name)}
                  className={`p-2 rounded-lg transition-colors ${
                    isDarkMode
                      ? 'text-fsgu-silver-400 hover:bg-red-500/20 hover:text-red-400'
                      : 'text-fsgu-silver-500 hover:bg-red-500/10 hover:text-red-500'
                  }`}
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}

          {/* No results from search */}
          {filteredPlayers.length === 0 && searchTerm && (
            <div className="px-4 py-8 text-center">
              <p className={isDarkMode ? 'text-fsgu-silver-400' : 'text-fsgu-silver-600'}>
                No players match "{searchTerm}"
              </p>
            </div>
          )}
        </div>
      )}

      {/* Summary */}
      {players.length > 0 && (
        <div
          className={`text-sm ${
            isDarkMode ? 'text-fsgu-silver-400' : 'text-fsgu-silver-600'
          }`}
        >
          Showing {filteredPlayers.length} of {players.length} players
        </div>
      )}

      {/* Import Modal */}
      {showImport && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div
            className={`w-full max-w-2xl rounded-xl shadow-xl ${
              isDarkMode ? 'bg-fsgu-gray-800' : 'bg-white'
            }`}
          >
            {/* Modal Header */}
            <div
              className={`flex items-center justify-between px-6 py-4 border-b ${
                isDarkMode ? 'border-fsgu-gray-700' : 'border-fsgu-silver-200'
              }`}
            >
              <h2
                className={`text-xl font-bold ${
                  isDarkMode ? 'text-fsgu-silver-100' : 'text-fsgu-silver-900'
                }`}
              >
                Import Draw from Excel
              </h2>
              <button
                onClick={() => {
                  setShowImport(false);
                  setImportData([]);
                  setImportFileName('');
                  if (fileInputRef.current) fileInputRef.current.value = '';
                }}
                className={`p-2 rounded-lg ${
                  isDarkMode
                    ? 'text-fsgu-silver-400 hover:bg-fsgu-gray-700'
                    : 'text-fsgu-silver-500 hover:bg-fsgu-silver-100'
                }`}
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="px-6 py-4 space-y-4">
              {/* Template download */}
              <div
                className={`p-4 rounded-lg border-2 border-dashed ${
                  isDarkMode ? 'border-fsgu-gray-600 bg-fsgu-gray-900' : 'border-fsgu-silver-300 bg-fsgu-silver-50'
                }`}
              >
                <p className={`font-medium mb-2 ${isDarkMode ? 'text-fsgu-silver-200' : 'text-fsgu-silver-800'}`}>
                  Excel Format:
                </p>
                <div className={`text-sm p-3 rounded mb-3 overflow-x-auto ${
                  isDarkMode ? 'bg-fsgu-gray-800 text-fsgu-silver-300' : 'bg-white text-fsgu-silver-700'
                }`}>
                  <table className="w-full text-left text-xs">
                    <thead>
                      <tr className={`border-b ${isDarkMode ? 'border-fsgu-gray-700' : 'border-fsgu-silver-200'}`}>
                        <th className="pb-2 pr-3 font-medium">Player Name</th>
                        <th className="pb-2 pr-3 font-medium">Club</th>
                        <th className="pb-2 pr-3 font-medium">Handicap</th>
                        <th className="pb-2 pr-3 font-medium">Tee Time</th>
                        <th className="pb-2 pr-3 font-medium">Group</th>
                        <th className="pb-2 font-medium">Starting Tee</th>
                      </tr>
                    </thead>
                    <tbody className={isDarkMode ? 'text-fsgu-silver-500' : 'text-fsgu-silver-400'}>
                      <tr>
                        <td className="py-1 pr-3">John Smith</td>
                        <td className="py-1 pr-3">Bloem GC</td>
                        <td className="py-1 pr-3">5.2</td>
                        <td className="py-1 pr-3">07:00</td>
                        <td className="py-1 pr-3">1</td>
                        <td className="py-1">1</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <button
                  onClick={handleDownloadTemplate}
                  className={`inline-flex items-center gap-2 px-3 py-1.5 rounded text-sm font-medium ${
                    isDarkMode
                      ? 'bg-fsgu-gray-700 text-fsgu-silver-300 hover:bg-fsgu-gray-600'
                      : 'bg-white text-fsgu-silver-700 hover:bg-fsgu-silver-100 border border-fsgu-silver-300'
                  }`}
                >
                  <Download className="w-4 h-4" />
                  Download Template (.xlsx)
                </button>
              </div>

              {/* File Upload */}
              <div>
                <label
                  className={`block text-sm font-medium mb-2 ${
                    isDarkMode ? 'text-fsgu-silver-300' : 'text-fsgu-silver-700'
                  }`}
                >
                  Upload Excel File
                </label>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={handleFileSelect}
                  className="hidden"
                />
                <div
                  onClick={() => fileInputRef.current?.click()}
                  className={`p-6 rounded-lg border-2 border-dashed cursor-pointer transition-colors ${
                    isDarkMode
                      ? 'border-fsgu-gray-600 hover:border-fsgu-green-500/50 bg-fsgu-gray-800'
                      : 'border-fsgu-silver-300 hover:border-fsgu-green-500/50 bg-fsgu-silver-50'
                  }`}
                >
                  <div className="text-center">
                    <FileSpreadsheet
                      className={`w-10 h-10 mx-auto mb-2 ${
                        isDarkMode ? 'text-fsgu-silver-500' : 'text-fsgu-silver-400'
                      }`}
                    />
                    {importFileName ? (
                      <p className={`font-medium ${isDarkMode ? 'text-fsgu-green-400' : 'text-fsgu-green-600'}`}>
                        {importFileName}
                      </p>
                    ) : (
                      <p className={isDarkMode ? 'text-fsgu-silver-400' : 'text-fsgu-silver-600'}>
                        Click to select an Excel file (.xlsx)
                      </p>
                    )}
                    {importData.length > 0 && (
                      <p className={`text-sm mt-1 ${isDarkMode ? 'text-fsgu-silver-500' : 'text-fsgu-silver-500'}`}>
                        {importData.length} players found
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* Preview */}
              {importData.length > 0 && (
                <div
                  className={`p-3 rounded-lg max-h-40 overflow-auto ${
                    isDarkMode ? 'bg-fsgu-gray-900' : 'bg-fsgu-silver-50'
                  }`}
                >
                  <p className={`text-sm font-medium mb-2 ${isDarkMode ? 'text-fsgu-silver-300' : 'text-fsgu-silver-700'}`}>
                    Preview (first 5):
                  </p>
                  <table className="w-full text-sm">
                    <tbody>
                      {importData.slice(0, 5).map((row, i) => (
                        <tr key={i} className={isDarkMode ? 'text-fsgu-silver-400' : 'text-fsgu-silver-600'}>
                          <td className="py-1 pr-2">{row.player_name}</td>
                          <td className="py-1 pr-2">{row.club}</td>
                          <td className="py-1 pr-2">{row.handicap}</td>
                          <td className="py-1">{row.tee_time}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {importData.length > 5 && (
                    <p className={`text-xs mt-2 ${isDarkMode ? 'text-fsgu-silver-500' : 'text-fsgu-silver-500'}`}>
                      ...and {importData.length - 5} more
                    </p>
                  )}
                </div>
              )}

              {players.length > 0 && (
                <div
                  className={`p-3 rounded-lg text-sm ${
                    isDarkMode ? 'bg-amber-500/10 text-amber-400' : 'bg-amber-500/10 text-amber-600'
                  }`}
                >
                  <AlertCircle className="w-4 h-4 inline mr-2" />
                  This will replace the existing {players.length} players in the draw
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div
              className={`flex justify-end gap-3 px-6 py-4 border-t ${
                isDarkMode ? 'border-fsgu-gray-700' : 'border-fsgu-silver-200'
              }`}
            >
              <button
                onClick={() => {
                  setShowImport(false);
                  setImportData([]);
                  setImportFileName('');
                  if (fileInputRef.current) fileInputRef.current.value = '';
                }}
                className={`px-4 py-2 rounded-lg font-medium ${
                  isDarkMode
                    ? 'bg-fsgu-gray-700 text-fsgu-silver-300 hover:bg-fsgu-gray-600'
                    : 'bg-fsgu-silver-100 text-fsgu-silver-700 hover:bg-fsgu-silver-200'
                }`}
              >
                Cancel
              </button>
              <button
                onClick={handleImport}
                disabled={importData.length === 0 || loading}
                className="btn btn-primary disabled:opacity-50"
              >
                {loading ? (
                  <>
                    <RefreshCw className="w-4 h-4 animate-spin mr-2" />
                    Importing...
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4 mr-2" />
                    Import {importData.length > 0 ? `(${importData.length})` : ''}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminTournamentDraw;